import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/shared/api/fetchApi';
import Button from '@/components/ui/button/Button';
import Select from '@/components/form/Select';
import Label from '@/components/form/Label';
import { ClientsFilters } from '../hooks/useClients';
import { DateRangeFilterModal } from './DateRangeFilterModal';
import { useTranslation } from 'react-i18next';

interface ClientFiltersProps {
  currentFilters: ClientsFilters;
  onFilterChange: (filters: ClientsFilters) => void;
}

interface CountryFiltersResponse {
  country: string[];
}

const ALL_OPTION = { value: '', label: 'All' };

const VERIFICATION_STATUSES = [
  ALL_OPTION,
  { value: 'new', label: 'clients.filters.status.new' },
  { value: 'verified', label: 'clients.filters.status.verified' },
  { value: 'verification_needed', label: 'clients.filters.status.verification_needed' }
];

export const ClientFilters: React.FC<ClientFiltersProps> = ({
  currentFilters,
  onFilterChange
}) => {
  const { t } = useTranslation();
  // Get available countries from API
  const { data: countriesData } = useQuery<CountryFiltersResponse>({
    queryKey: ['client-filters'],
    queryFn: () => fetchApi('/api/v1/clients/filters')
  });

  const [isDateFilterModalOpen, setIsDateFilterModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: currentFilters.registration_start_date ? new Date(currentFilters.registration_start_date) : null,
    endDate: currentFilters.registration_end_date ? new Date(currentFilters.registration_end_date) : null
  });

  const handleDateRangeApply = (newDateRange: { startDate: Date | null; endDate: Date | null }) => {
    setDateRange(newDateRange);
    onFilterChange({
      ...currentFilters,
      registration_start_date: newDateRange.startDate ? newDateRange.startDate.toISOString().split('T')[0] : undefined,
      registration_end_date: newDateRange.endDate ? newDateRange.endDate.toISOString().split('T')[0] : undefined
    });
  };

  const formatDateRange = () => {
    if (!dateRange.startDate && !dateRange.endDate) return t('clients.filters.date_range');

    const formatDate = (date: Date | null) => {
      if (!date) return '';
      return date.toLocaleDateString('default', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    if (dateRange.startDate && dateRange.endDate) {
      return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;
    }

    return formatDate(dateRange.startDate || dateRange.endDate);
  };

  const handleVerificationStatusChange = (status: string | undefined) => {
    onFilterChange({
      ...currentFilters,
      verification_status: status ? status as 'new' | 'verified' | 'verification_needed' : undefined
    });
  };

  const handleCountryChange = (country: string | undefined) => {
    onFilterChange({
      ...currentFilters,
      country: country || undefined
    });
  };

  const handleSortChange = () => {
    const currentSort = currentFilters.sort?.registration_date || 'asc';
    const newSort = currentSort === 'asc' ? 'desc' : 'asc';
    onFilterChange({
      ...currentFilters,
      sort: {
        registration_date: newSort
      }
    });
  };

  // Функция для проверки наличия активных фильтров
  const hasActiveFilters = (): boolean => {
    return !!(
      currentFilters.verification_status ||
      currentFilters.country ||
      currentFilters.registration_start_date ||
      currentFilters.registration_end_date
    );
  };

  const handleResetFilters = () => {
    setDateRange({ startDate: null, endDate: null });
    onFilterChange({
      // Сбрасываем все фильтры, оставляя только текущую сортировку
      verification_status: undefined,
      country: undefined,
      registration_start_date: undefined,
      registration_end_date: undefined,
      sort: currentFilters.sort
    });
  };

  const countryOptions = [
    ALL_OPTION,
    ...(countriesData?.country?.map(country => ({
      value: country,
      label: new Intl.DisplayNames(['en'], { type: 'region' }).of(country) || country
    })) || [])
  ];

  // Replace the existing date picker with a button that opens the modal
  const dateFilterSection = (
    <div className="flex-1 w-full max-w-[200px]">
      <Label>{t('clients.filters.registration_date')}</Label>
      <Button
        variant="outline"
        onClick={() => setIsDateFilterModalOpen(true)}
        className="w-full justify-start !py-3 !px-4 text-left"
      >
        <span className={dateRange.startDate || dateRange.endDate ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500'}>
          {formatDateRange()}
        </span>
      </Button>

      <DateRangeFilterModal
        isOpen={isDateFilterModalOpen}
        onClose={() => setIsDateFilterModalOpen(false)}
        onApply={handleDateRangeApply}
        initialDateRange={dateRange}
      />
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center w-full">
      {dateFilterSection}

      {/* Verification Status Filter */}
      <div className="flex-1 w-full md:max-w-[180px]">
        <Label>{t('clients.filters.verification_status')}</Label>
        <Select
          value={currentFilters.verification_status || ''}
          onChange={handleVerificationStatusChange}
          options={VERIFICATION_STATUSES.map(status => ({
            ...status,
            label: t(status.label)
          }))}
          className="w-full"
          placeholder={t('clients.filters.verification_status')}
        />
      </div>

      {/* Country Filter */}
      <div className="flex-1 w-full md:max-w-[180px]">
        <Label>{t('clients.filters.country')}</Label>
        <Select
          value={currentFilters.country || ''}
          onChange={handleCountryChange}
          options={countryOptions}
          className="w-full"
          placeholder={t('clients.filters.country')}
        />
      </div>

      {hasActiveFilters() && (
        <div className="md:max-w-[180px]">
          <Button
            variant="outline"
            size="md"
            onClick={handleResetFilters}
            className="md:mt-[26px] !py-3"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t('clients.filters.reset_filters')}
          </Button>
        </div>
      )}

      {/* Sort and Reset Buttons */}
      <div className="w-fit md:ml-auto">
        <Button
          variant="outline"
          size="md"
          onClick={handleSortChange}
          className="!py-3 md:mt-[26px]"
        >
          {t('clients.filters.sort_by_date')}
          <span className="text-[18px]">
            {(currentFilters.sort?.registration_date || 'asc') === 'asc' ? '↑' : '↓'}
          </span>
        </Button>
      </div>
    </div>
  );
};
